// components/NavBar.jsx
// Top navigation bar. Reads auth from AuthContext (not props).

import React, { useState, useEffect } from "react";
import LogoWordmark from "./atoms/LogoWordmark";
import { useTheme } from "../context/ThemeContext";
import { useAuth  } from "../context/AuthContext";
import "./NavBar.css";

function DarkToggle() {
  const { dark, toggle } = useTheme();
  return (
    <button onClick={toggle} title={dark ? "Light mode" : "Dark mode"} aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      style={{ width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", background: dark ? "#6b4fe0" : "#d0cec9", position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: dark ? 19 : 3, transition: "left 0.22s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
    </button>
  );
}

export default function NavBar({ page, nav }) {
  const { t, dark }      = useTheme();
  const { user, logout, updateProfile } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [instituteDraft, setInstituteDraft] = useState("");
  const [educationDraft, setEducationDraft] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const closeMenuOnScroll = () => setMenuOpen(false);
    window.addEventListener("scroll", closeMenuOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", closeMenuOnScroll);
  }, [menuOpen]);

  useEffect(() => {
    if (!dropOpen) return;
    const close = (e) => { if (!e.target.closest("#avatar-menu")) setDropOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [dropOpen]);

  useEffect(() => {
    if (!profileOpen) return;
    const close = (e) => {
      if (!e.target.closest("#profile-panel") && !e.target.closest("#profile-nav-button")) setProfileOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [profileOpen]);

  const goTo = (key) => {
    setMenuOpen(false);
    nav(key);
  };

  const startProfileEdit = () => {
    setInstituteDraft(user.instituteName || "");
    setEducationDraft(user.educationLevel || "");
    setProfileError("");
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileError("");
    try {
      await updateProfile(instituteDraft.trim(), educationDraft);
      setEditingProfile(false);
    } catch (err) {
      setProfileError(err.message || "Unable to update profile.");
    } finally {
      setProfileSaving(false);
    }
  };

  const navBg = scrolled
    ? (dark ? "rgba(15,17,23,0.97)" : "rgba(252,251,249,0.97)")
    : (dark ? "rgba(15,17,23,0.6)"  : "rgba(252,251,249,0.6)");

  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center", background: navBg, backdropFilter: "blur(20px)", borderBottom: `0.5px solid ${scrolled ? t.border : "transparent"}`, transition: "all 0.3s" }}>
      <div className="navbar-inner" style={{ maxWidth: 1200, margin: "0 auto", width: "100%", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <LogoWordmark onClick={() => nav("home")} />
        <div className="navbar-desktop-actions" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {[{ k: "home", l: "Archive" }, { k: "potd", l: "POTD" }, { k: "leaderboard", l: "Leaderboard" }, { k: "contact", l: "Contact" }].map(({ k, l }) => (
            <button key={k} onClick={() => nav(k)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px 13px", borderRadius: 8, fontSize: 13, fontWeight: page === k ? 600 : 400, color: page === k ? t.text : t.text2, transition: "all 0.15s" }}>
              {l}
            </button>
          ))}
          {user && (
            <button id="profile-nav-button" onClick={() => goTo("profile")} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px 13px", borderRadius: 8, fontSize: 13, fontWeight: page === "profile" ? 600 : 400, color: page === "profile" ? t.text : t.text2, transition: "all 0.15s" }}>
              Profile
            </button>
          )}
          <div style={{ marginLeft: 6, marginRight: 6 }}><DarkToggle /></div>
          {user ? (
            <div id="avatar-menu" style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {user.role === "admin" && (
                  <button onClick={() => nav("admin")} style={{ background: page === "admin" ? t.primary : "transparent", color: page === "admin" ? t.primaryFg : t.primary, border: `0.5px solid ${t.primary}`, borderRadius: 8, padding: "5px 13px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    Admin
                  </button>
                )}
                <button onClick={() => { setDropOpen((o) => !o); setProfileOpen(false); }} style={{ width: 34, height: 34, borderRadius: "50%", background: user.avatar ? "transparent" : t.primary, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                  {user.avatar
                    ? <img src={user.avatar} alt={user.name} style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                    : <span style={{ fontSize: 13, color: t.primaryFg, fontWeight: 700 }}>{user.name.charAt(0).toUpperCase()}</span>
                  }
                </button>
              </div>
              {profileOpen && (
                <div id="profile-panel" role="dialog" aria-label="Profile" style={{ position: "fixed", top: 68, right: 24, width: "min(320px, calc(100vw - 32px))", boxSizing: "border-box", background: t.cardBg, border: `0.5px solid ${t.border}`, borderRadius: 12, padding: "6px 0", boxShadow: dark ? "0 8px 32px rgba(0,0,0,0.55)" : "0 8px 32px rgba(0,0,0,0.10)", zIndex: 200 }}>
                  <div style={{ padding: "12px 16px 14px", borderBottom: `0.5px solid ${t.border}` }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: t.text3, letterSpacing: "0.06em", margin: "0 0 10px" }}>PROFILE</p>
                    <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: "0 0 2px" }}>{user.name}</p>
                    <p style={{ fontSize: 11, color: t.text3, margin: 0 }}>{user.email}</p>
                    {editingProfile ? (
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: "block", fontSize: 10, color: t.text3, marginBottom: 4 }}>INSTITUTE NAME</label>
                        <input value={instituteDraft} onChange={(e) => setInstituteDraft(e.target.value)} placeholder="Your institute or university" maxLength={120}
                          style={{ width: "100%", boxSizing: "border-box", padding: "7px 8px", border: `0.5px solid ${t.border2}`, borderRadius: 6, background: t.inputBg, color: t.text, fontSize: 11 }} />
                        <label style={{ display: "block", fontSize: 10, color: t.text3, margin: "8px 0 4px" }}>EDUCATION LEVEL</label>
                        <select value={educationDraft} onChange={(e) => setEducationDraft(e.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: "7px 8px", border: `0.5px solid ${t.border2}`, borderRadius: 6, background: t.inputBg, color: t.text, fontSize: 11 }}>
                          <option value="">Select your level</option>
                          {['Undergraduate', '12th', '10th', 'Postgraduate', 'Schooling', 'PhD'].map((level) => <option key={level} value={level}>{level}</option>)}
                        </select>
                        {profileError && <p style={{ color: "#c0392b", fontSize: 10, margin: "5px 0 0" }}>{profileError}</p>}
                        <div style={{ display: "flex", gap: 6, marginTop: 7 }}>
                          <button onClick={saveProfile} disabled={profileSaving} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 6, padding: "5px 9px", fontSize: 11, cursor: profileSaving ? "wait" : "pointer" }}>{profileSaving ? "Saving..." : "Save"}</button>
                          <button onClick={() => setEditingProfile(false)} style={{ background: "none", color: t.text2, border: `0.5px solid ${t.border2}`, borderRadius: 6, padding: "5px 9px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={{ fontSize: 11, color: t.text2, margin: "7px 0 0" }}>{user.instituteName || "Institute not added"}</p>
                        {user.educationLevel && <p style={{ fontSize: 11, color: t.text2, margin: "3px 0 0" }}>{user.educationLevel}</p>}
                        <button onClick={startProfileEdit} style={{ background: "none", border: "none", padding: 0, marginTop: 5, color: t.primary, fontSize: 11, cursor: "pointer" }}>Edit profile</button>
                      </>
                    )}
                    <div style={{ display: "flex", gap: 18, marginTop: 10 }}>
                      <div>
                        <p style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>{user.potdSolvedCount || 0}</p>
                        <p style={{ fontSize: 10, color: t.text3, margin: "2px 0 0" }}>POTD solved</p>
                      </div>
                      <div>
                        <p style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>{user.points || 0}</p>
                        <p style={{ fontSize: 10, color: t.text3, margin: "2px 0 0" }}>points</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {dropOpen && (
                <div style={{ position: "fixed", top: 68, right: 24, background: t.cardBg, border: `0.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0", minWidth: 140, boxShadow: dark ? "0 8px 32px rgba(0,0,0,0.55)" : "0 8px 32px rgba(0,0,0,0.10)", zIndex: 200 }}>
                  <button onClick={() => { setDropOpen(false); logout(); nav("home"); }}
                    style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "10px 16px", fontSize: 13, color: "#c0392b", fontWeight: 500, textAlign: "left" }}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => nav("login")} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
              Sign in
            </button>
          )}
        </div>
        <button className="navbar-menu-button" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
          <span />
          <span />
          <span />
        </button>
        {menuOpen && (
          <div className="navbar-mobile-menu">
            <button className="navbar-mobile-link" onClick={() => goTo("home")}>
              Archive
            </button>
            <button className="navbar-mobile-link" onClick={() => goTo("potd")}>
              POTD
            </button>
            <button className="navbar-mobile-link" onClick={() => goTo("leaderboard")}>
              Leaderboard
            </button>
            <button className="navbar-mobile-link" onClick={() => goTo("contact")}>
              Contact
            </button>
            {user && (
              <button className="navbar-mobile-link" onClick={() => goTo("profile")}>
                Profile
              </button>
            )}
            <div className="navbar-mobile-setting">
              <span>Appearance</span>
              <DarkToggle />
            </div>
            {user ? (
              <>
                {user.role === "admin" && (
                  <button className="navbar-mobile-link" onClick={() => goTo("admin")}>
                    Admin
                  </button>
                )}
                <button className="navbar-mobile-link navbar-mobile-signout" onClick={() => { setMenuOpen(false); logout(); nav("home"); }}>
                  Sign out
                </button>
              </>
            ) : (
              <button className="navbar-mobile-signin" onClick={() => goTo("login")}>
                Sign in
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
